import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Wrench } from "lucide-react";

import { ApiError } from "../../lib/api";
import { Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { Button, Select, Textarea } from "../../components/ui";
import {
  crearOrden,
  numeroOT,
  type OrdenTrabajoCreate,
  type PrioridadOT,
} from "../mantenimiento/api";
import { TIPO_LABELS, folioAtencion, type Atencion } from "./api";

/**
 * Deriva una atención a una orden de trabajo de mantenimiento (una fuga
 * reclamada, un medidor que gotea). La OT queda enlazada a la atención con
 * `atencion_id`, así Mantenimiento sabe quién la pidió.
 *
 * Se crea desde acá, como la derivación de /consumos, porque la pantalla de
 * OT no recibe parámetros de una OT nueva.
 */
export function DerivarOtModal({
  atencion,
  onClose,
}: {
  atencion: Atencion;
  onClose: () => void;
}): JSX.Element {
  const toast = useToast();
  const [descripcion, setDescripcion] = useState(
    `Atención N° ${folioAtencion(atencion)} (${TIPO_LABELS[atencion.tipo].toLowerCase()}) de ${atencion.usuario_nombre}:\n${atencion.descripcion}`,
  );
  const [prioridad, setPrioridad] = useState<PrioridadOT>(
    atencion.tipo === "Emergencia" ? "Critica" : "Media",
  );

  const qc = useQueryClient();
  // Un doble clic alcanzaba a mandar dos POST antes de que `isPending`
  // deshabilitara el botón (así nacieron dos OT iguales).
  const enviando = useRef(false);

  const m = useMutation({
    mutationFn: () => {
      // `atencion_id` lo acepta el backend; el tipo del admin aún no lo
      // declara (mantenimiento/api.ts), por eso va como extensión.
      const payload: OrdenTrabajoCreate & { atencion_id: number } = {
        tipo: atencion.tipo === "Emergencia" ? "Emergencia" : "Correctivo",
        prioridad,
        descripcion_solicitud: descripcion.trim(),
        atencion_id: atencion.id,
      };
      return crearOrden(payload);
    },
    onSuccess: (ot) => {
      toast.success(`OT ${numeroOT(ot)} creada en Mantenimiento.`);
      void qc.invalidateQueries({ queryKey: ["atenciones"] });
      void qc.invalidateQueries({ queryKey: ["mantenimiento", "ordenes"] });
      onClose();
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.code === "atencion_ya_derivada") {
        // Ya existe: se avisa con su N° y la lista pasa a mostrarla.
        toast.error(err.message);
        void qc.invalidateQueries({ queryKey: ["atenciones"] });
        onClose();
        return;
      }
      toast.error(err instanceof ApiError ? err.message : "No se pudo crear la OT");
    },
    onSettled: () => {
      enviando.current = false;
    },
  });

  function crear(): void {
    if (enviando.current) return;
    enviando.current = true;
    m.mutate();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Derivar a orden de trabajo"
      subtitle={`Atención N° ${folioAtencion(atencion)}`}
      icon={Wrench}
      size="md"
      closeOnBackdrop={!m.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={m.isPending}>
            Cancelar
          </Button>
          <Button
            icon={Wrench}
            disabled={descripcion.trim().length < 5 || m.isPending}
            onClick={crear}
          >
            {m.isPending ? "Creando…" : "Crear orden de trabajo"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-neutral-700">
          Queda como OT pendiente en Mantenimiento, lista para asignarle un
          técnico. La atención sigue abierta hasta que la respondas.
        </p>
        <Select
          label="Prioridad"
          value={prioridad}
          onChange={(e) => setPrioridad(e.target.value as PrioridadOT)}
          containerClassName="w-48"
        >
          <option value="Baja">Baja</option>
          <option value="Media">Media</option>
          <option value="Alta">Alta</option>
          <option value="Critica">Crítica</option>
        </Select>
        <Textarea
          label="Qué hay que hacer"
          rows={5}
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
        />
      </div>
    </Modal>
  );
}
